import React from "react";
import { Formik, FormikProps } from "formik";
import { Grid } from "@material-ui/core";
import * as Yup from "yup";

import { Values } from "Tools/Values";
import { STFormWrapper } from "@src/Components/Ui/STFormWrapper";
import renderCommonFormField from "@src/Components/Common/FormFields/renderCommonFormField";
import FormSwitch from "@src/Components/Common/FormFields/FormSwitch/FormSwitch";
import { inject, observer } from "mobx-react";
import { IValuesStore } from "@src/Stores/Values/types";
import { IManagementStore } from "@src/Stores/Management/types";
import { STFormSimpleSelect, STGrid } from "./styles";
import { STFormTitleTypography } from "@src/Components/Ui/STFormTitleTypohraphy";
import { ButtonsForm } from "@src/Components/Common/ButtonsForm/ButtonsForm";
import { Position } from "@src/Interfaces/Entity/Position";
import { STContentWrapper } from "@src/Components/Common/SimpleModal/styles";
import {computed} from "mobx";
import {IAuthStore} from "Stores/Auth/types";

const positionSchema = (positions: Position[]) =>
  Yup.object().shape({
    title: Yup.string()
        .required(() => {
          return Values.getLocalizationValue("fieldRequired");
        })
        .test("containsTrailingSpaces", Values.getLocalizationValue("fieldCantContainTrailingSpaces"), function(value: string) {
          if (!value) {
            return true;
          }
          return value.length === value.trim().length;
        }),
    name: Yup.string()
      .required(() => {
        return Values.getLocalizationValue("fieldRequired");
      })
      .test("containsTrailingSpaces", Values.getLocalizationValue("fieldCantContainTrailingSpaces"), function(value: string) {
        if (!value) {
          return true;
        }
        return value.length === value.trim().length;
      })
      .test("isNameExists", Values.getLocalizationValue("nameAlreadyExists"), function(value: string) {
        if (!value) {
          return true;
        }
        const filteredPositions = positions.filter(position => position.name.toLowerCase() === value.toLowerCase());
        if (filteredPositions.length === 0) {
          return true;
        }
        if (this.parent.id !== "00000000-0000-0000-0000-000000000000" && filteredPositions[0].id === this.parent.id) {
          return true;
        }
        return false;
      }),
    permissionGroup: Yup.string().required(() => {
      return Values.getLocalizationValue("fieldRequired");
    }),
  });

interface IProps {
  title: string;
  initialValues: Position;
  onSubmit: (values: Position) => void;
  onClose: () => void;
  values?: IValuesStore;
  management?: IManagementStore;
  auth?: IAuthStore;
}

interface ISelectItem {
  id: string;
  name: string;
  divider: boolean;
}

@inject("values", "management", "auth")
@observer
class PositionForm extends React.Component<IProps> {
  @computed get isAdminPosition() {
    return this.props.initialValues.id == this.props.values?.common["AdminPositionId"];
  }

  @computed get adminPermissionGroupId() {
    return this.props.values.common["AdminPermissionGroupId"];
  }


  @computed get availablePermissionGroups() {
    let permissionGroups = this.props.management.activePermissionGroups;
    permissionGroups.forEach((item) => {
      if (item.id == this.adminPermissionGroupId && !this.props.auth.isAdmin) {
        item.isDisabledAdminPosition = true;
      }
    });
    return permissionGroups;
  }
  
  @computed get isPGInputDisabled() {
    let disabled: boolean = false;
    if (!this.props.auth.isAdmin) {
      if (this.props.initialValues.permissionGroup == this.props.values.common["AdminPermissionGroupId"]) {
        disabled = true;
      }
    }
    return disabled || this.isAdminPosition;
  }

  @computed get isPGWhenActivelyAssignedInputDisabled() {
    let disabled: boolean = false;
    if (!this.props.auth.isAdmin) {
      if (this.props.initialValues.permissionGroupWhenActivelyAssigned == this.props.values.common["AdminPermissionGroupId"]) {
        disabled = true;
      }
    }
    return disabled || this.isAdminPosition;
  }

  lastPickedMaster: boolean = false;
  
  render() {
    return (
      <Formik
        initialValues={this.props.initialValues}
        validationSchema={() => positionSchema(this.props.management.positions)}
        onSubmit={this.props.onSubmit}
        render={(formikProps: FormikProps<Position>) => (
          <STFormWrapper onSubmit={formikProps.handleSubmit}>
            <STFormTitleTypography>{this.props.title}</STFormTitleTypography>
            <STContentWrapper>
              {renderCommonFormField<Position>("title", Values.getLocalizationValue("title"), formikProps)}
              {renderCommonFormField<Position>("name", Values.getLocalizationValue("name"), formikProps)}
              {renderCommonFormField<Position>("description", Values.getLocalizationValue("description"), formikProps)}
              <Grid container spacing={16} justify="flex-start" alignItems="center">
                <Grid item xs={12}>
                  <STFormSimpleSelect<Position, ISelectItem>
                    fieldName="permissionGroup"
                    items={this.availablePermissionGroups}
                    keyAsTitle="name"
                    keyAsValue="id"
                    keyAsOptionDisabled={"isDisabledAdminPosition"}
                    keyAsDescription="description"
                    formikProps={formikProps}
                    label={Values.getLocalizationValue("permissionGroup")}
                    disabled={this.isPGInputDisabled}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={16} justify="flex-start" alignItems="center">
                <Grid item xs={12}>
                  <STFormSimpleSelect<Position, ISelectItem>
                    fieldName="permissionGroupWhenActivelyAssigned"
                    items={this.availablePermissionGroups}
                    keyAsTitle="name"
                    keyAsValue="id"
                    keyAsOptionDisabled={"isDisabledAdminPosition"}
                    keyAsDescription="description"
                    formikProps={formikProps}
                    label={Values.getLocalizationValue("permissionGroupWhenActivelyAssigned")}
                    disabled={this.isPGWhenActivelyAssignedInputDisabled}
                  />
                </Grid>
              </Grid>
              <STGrid container spacing={16} justify="flex-start" alignItems="center">
                <Grid item>{Values.getLocalizationValue("shouldAlwaysBeAssigned")}</Grid>
                <Grid item>
                  <FormSwitch<Position>
                    trueAs={true}
                    falseAs={false}
                    fieldName="shouldAlwaysBeAssigned"
                    formikProps={formikProps}
                  />
                </Grid>
              </STGrid>
              <Grid container spacing={16} justify="flex-start" alignItems="center">
                <Grid item>{Values.getLocalizationValue("allowMultipleActiveAssigns")}</Grid>
                <Grid item>
                  <FormSwitch<Position>
                    trueAs={true}
                    falseAs={false}
                    fieldName="allowMultipleActiveAssigns"
                    formikProps={formikProps}
                    disabled={!this.props.management.options.allowToSetupMultipleAssigns}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={16} justify="flex-start" alignItems="center">
                <Grid item>{Values.getLocalizationValue("active")}</Grid>
                <Grid item>
                  <FormSwitch<Position> 
                    trueAs={this.props.values.states.Active}
                    falseAs={this.props.values.states.Deactivated}
                    fieldName="state"
                    formikProps={formikProps}
                    disabled={this.isAdminPosition}
                  />
                </Grid>
              </Grid>
            </STContentWrapper>
            <ButtonsForm onSubmit={formikProps.handleSubmit} onClose={this.props.onClose} />
          </STFormWrapper>
        )}
      />
    );
  }
}

export default PositionForm;
